/**
 * Member has no shift-boundary activity on today's workline (e.g. division head not shifted in).
 *
 * @param {object|null|undefined} member
 * @param {{ isShiftNotShiftedIn?: (status: unknown) => boolean }} [tt]
 */
export function isRightDrawerMemberNotShiftedIn(member, tt) {
    if (!member) {
        return false;
    }
    if (tt?.isShiftNotShiftedIn?.(member.shift_status)) {
        return true;
    }
    return !member.recent_shift_event_at;
}

/**
 * Member shifted out today (on workline with Shift_Out), not merely a stale profile status.
 *
 * @param {object|null|undefined} member
 * @param {{ isShiftOut: (status: unknown) => boolean }} tt
 */
export function isRightDrawerMemberShiftedOut(member, tt) {
    if (!member?.recent_shift_event_at) {
        return false;
    }
    return tt.isShiftOut(member.shift_status);
}

/**
 * When true, hide timer badges, task/shift overlay icons, and the assigner mini-avatar on the rail.
 *
 * @param {object|null|undefined} member
 * @param {{ isShiftOut: (status: unknown) => boolean }} tt
 */
export function shouldHideRightDrawerMemberRailTaskShiftIcons(member, tt) {
    if (!member) {
        return true;
    }
    if (isRightDrawerMemberNotShiftedIn(member, tt)) {
        return true;
    }
    if (isRightDrawerMemberShiftedOut(member, tt)) {
        return true;
    }
    return (
        !member.is_active &&
        Boolean(member.is_organization_user) &&
        !member.is_agent
    );
}

const emptyShiftUi = {
    showPauseZone: false,
    primaryBadge: null,
    overlay: null,
};

const RESUME_TIMES_UP_MS = 1000 * 60;

/**
 * Format remaining/overdue resume time like the workline rail timer
 * (`utilities().getTimeDifference(..., false)`).
 *
 * @param {Date} future_date
 * @param {Date} now
 * @returns {string}
 */
function formatResumeTimeLeft(future_date, now) {
    if (future_date < now) {
        return ":00";
    }

    const diff_ms = Math.abs(future_date - now);
    const diff_hours = Math.floor(diff_ms / (1000 * 60 * 60));
    const diff_minutes = Math.floor(
        (diff_ms % (1000 * 60 * 60)) / (1000 * 60),
    );

    let result = "";
    if (diff_hours > 0) {
        result += `${diff_hours}`;
    }
    result += `:${diff_minutes.toString().padStart(2, "0")}`;

    return result;
}

/**
 * Parse workline/profile resume timestamps (naive strings treated as UTC).
 * Kept local to avoid a circular import with memberShiftAvatarState.
 *
 * @param {string} shiftResumeAt
 * @returns {Date|null}
 */
function parseShiftResumeAt(shiftResumeAt) {
    let normalized = String(shiftResumeAt);
    if (normalized.slice(-1) !== "Z" && !normalized.includes("+")) {
        normalized = `${normalized}Z`;
    }
    normalized = normalized.replace(/\.(\d{3})\d+Z$/i, ".$1Z");

    const date = new Date(normalized);
    if (Number.isNaN(date.getTime())) {
        return null;
    }

    return date;
}

/**
 * @param {string|null|undefined} shiftResumeAt
 * @param {number|Date} [nowMs] - injectable clock for tests
 * @returns {{ time_left: string, times_up: boolean, resume_at: string }|null}
 */
export function buildShiftResumeEntryFromAt(shiftResumeAt, nowMs = Date.now()) {
    if (!shiftResumeAt) {
        return null;
    }

    const resume = parseShiftResumeAt(shiftResumeAt);
    if (!resume) {
        return null;
    }

    const now = nowMs instanceof Date ? nowMs : new Date(nowMs);
    if (Number.isNaN(now.getTime())) {
        return null;
    }

    const diff = resume.getTime() - now.getTime();
    const times_up = diff <= RESUME_TIMES_UP_MS;

    return {
        resume_at: shiftResumeAt,
        times_up,
        time_left: times_up
            ? formatResumeTimeLeft(now, resume)
            : formatResumeTimeLeft(resume, now),
    };
}

/**
 * Avatar overlay icon for the workline rail.
 * Active on-shift members with a running task get "running" (green dot).
 *
 * @param {object|null|undefined} member
 * @param {{ times_up?: boolean }|null|undefined} resumeEntry
 * @param {import('pinia').Store<'timeTrackerStore'>} tt
 * @returns {null | 'power' | 'moon' | 'inactive_pause' | 'accent_pause' | 'running'}
 */
export function resolveMemberShiftOverlay(member, resumeEntry, tt) {
    const shift = member?.shift_status;
    const task = member?.task_status;

    if (tt.isShiftPauseUnreachable(shift)) {
        return "power";
    }

    if (tt.isShiftPauseInActive(shift)) {
        return "moon";
    }

    if (resumeEntry?.times_up) {
        return "inactive_pause";
    }

    if (
        tt.isShiftPause(shift) &&
        (tt.isTaskStop(task) || tt.isTaskPause(task))
    ) {
        return "accent_pause";
    }

    if (tt.isTaskRunning(task)) {
        return "running";
    }

    return null;
}

/**
 * Effort Distribution only shows overlay icons for users paused on the current workday.
 *
 * @param {object|null|undefined} member
 * @param {{ times_up?: boolean }|null|undefined} resumeEntry
 * @param {import('pinia').Store<'timeTrackerStore'>} tt
 * @returns {null | 'power' | 'moon' | 'inactive_pause' | 'accent_pause' | 'running'}
 */
export function resolvePausedMemberShiftOverlay(member, resumeEntry, tt) {
    if (!tt.isShiftPauseOrUnreachableOrInActive(member?.shift_status)) {
        return null;
    }

    return resolveMemberShiftOverlay(member, resumeEntry, tt);
}

/**
 * Pure view-model for right-rail shift / task badges and overlay icon.
 * Keeps RightDrawer template declarative: one lookup per member, no nested store calls.
 *
 * @param {object|null|undefined} member
 * @param {{ time_left?: string, times_up?: boolean }|undefined|null} resumeEntry
 * @param {string} shiftSinceLabel - badge text for pause-unreachable / inactive timers
 * @param {import('pinia').Store<'timeTrackerStore'>} tt - useTimeTrackerStore()
 * @returns {{
 *   showPauseZone: boolean,
 *   primaryBadge: null | { kind: 'error_timer', content: string } | { kind: 'resume', content: string, color: string },
 *   overlay: null | 'power' | 'moon' | 'inactive_pause' | 'accent_pause' | 'running'
 * }}
 */
export function resolveRightDrawerMemberShiftUi(
    member,
    resumeEntry,
    shiftSinceLabel,
    tt,
) {
    if (shouldHideRightDrawerMemberRailTaskShiftIcons(member, tt)) {
        return emptyShiftUi;
    }

    const shift = member?.shift_status;

    const showPauseZone = tt.isShiftPauseOrUnreachableOrInActive(shift);

    let primaryBadge = null;
    if (showPauseZone) {
        if (
            tt.isShiftPauseUnreachable(shift) ||
            tt.isShiftPauseInActive(shift)
        ) {
            primaryBadge = { kind: "error_timer", content: shiftSinceLabel };
        } else if (member?.shift_resume_at) {
            primaryBadge = {
                kind: "resume",
                content: resumeEntry?.time_left ?? ":00",
                color: resumeEntry?.times_up
                    ? "error"
                    : "rgb(var(--v-theme-pause))",
            };
        }
    }

    const overlay = resolveMemberShiftOverlay(member, resumeEntry, tt);

    return { showPauseZone, primaryBadge, overlay };
}
